import React, { useState, useEffect, useRef, useCallback } from 'react';
import API_BASE, { api } from '../lib/api';

// ── FILE HELPERS ──
const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getFileIcon = (mime = '', name = '') => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (mime.startsWith('image/')) return '🖼️';
    if (mime === 'application/pdf' || ext === 'pdf') return '📄';
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return '🗜️';
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return '🎬';
    if (['mp3', 'wav', 'ogg', 'flac'].includes(ext)) return '🎵';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
    if (['ppt', 'pptx'].includes(ext)) return '📽️';
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json'].includes(ext)) return '💻';
    return '📎';
};

// ── THEMES ──
const THEMES = {
    dark: {
        name: 'dark', 
        bg: 'var(--premium-bg)', 
        sidebar: 'var(--surface)', 
        chat: 'var(--surface)',
        header: 'color-mix(in srgb, var(--surface) 92%, transparent)', 
        inputBar: 'color-mix(in srgb, var(--surface) 85%, transparent)',
        border: 'var(--border)', 
        card: 'var(--surface)', 
        text: 'var(--text-primary)', 
        subtext: 'var(--text-secondary)',
        muted: 'var(--text-muted)', 
        accent: '#10b981', 
        accentBg: 'rgba(16,185,129,0.15)',
        accentBorder: 'rgba(16,185,129,0.3)', 
        msgMe: '#10b981', 
        msgThem: 'var(--border)',
        msgMeText: '#ffffff', 
        msgThemText: 'var(--text-primary)', 
        inputBg: 'var(--surface-hover)',
        inputBorder: 'var(--border)', 
        placeholder: 'var(--text-muted)', 
        fileBg: 'rgba(16,185,129,0.1)',
        icon: '☀️', 
        label: 'Light Mode',
    },
    light: {
        name: 'light', 
        bg: 'var(--premium-bg)', 
        sidebar: 'var(--surface)', 
        chat: 'var(--surface)',
        header: 'color-mix(in srgb, var(--surface) 92%, transparent)', 
        inputBar: 'color-mix(in srgb, var(--surface) 92%, transparent)',
        border: 'var(--border)', 
        card: 'var(--surface)', 
        text: 'var(--text-primary)', 
        subtext: 'var(--text-secondary)',
        muted: 'var(--text-muted)', 
        accent: '#059669', 
        accentBg: 'rgba(5,150,105,0.08)',
        accentBorder: 'rgba(5,150,105,0.25)', 
        msgMe: '#059669', 
        msgThem: 'var(--surface)',
        msgMeText: '#ffffff', 
        msgThemText: 'var(--text-primary)', 
        inputBg: 'var(--surface)',
        inputBorder: 'var(--border)', 
        placeholder: 'var(--text-muted)', 
        fileBg: '#ecfdf5',
        icon: '🌙', 
        label: 'Dark Mode',
    }
};

export const WhisperMode = () => {
    const [theme, setTheme] = useState(() => {
        const isGlobalDark = document.documentElement.classList.contains('dark');
        return isGlobalDark ? THEMES.dark : THEMES.light;
    });
    const T = theme;
    const toggleTheme = () => {
        const next = T.name === 'dark' ? THEMES.light : THEMES.dark;
        setTheme(next);
        localStorage.setItem('whisper-theme', next.name);
    };

    const [userName, setUserName] = useState('');
    const [roomId, setRoomId] = useState('');
    const [joinStep, setJoinStep] = useState('initial'); // 'initial', 'create', 'joinCode'
    const [myClientId, setMyClientId] = useState(null);
    const [isJoined, setIsJoined] = useState(false);
    const [userList, setUserList] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const activeChatRef = useRef(null);
    const [messages, setMessages] = useState({});
    const [unreadCounts, setUnreadCounts] = useState({});
    const [inputText, setInputText] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showSidebar, setShowSidebar] = useState(true);
    const [pendingFile, setPendingFile] = useState(null); // { file, previewUrl }
    const [uploading, setUploading] = useState(false);
    const [remoteTypingMap, setRemoteTypingMap] = useState({}); // { clientId: timestamp }
    const typingTimeoutRef = useRef({});
    const incomingChunks = useRef({});
    const ws = useRef(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => { activeChatRef.current = activeChat; }, [activeChat]);
    const messagesContainerRef = useRef(null);
    const scrollToBottom = useCallback((isSmooth = false) => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: isSmooth ? 'smooth' : 'auto'
            });
        }
    }, []);

    useEffect(() => {
        scrollToBottom(false);
    }, [messages, activeChat, scrollToBottom]);
    useEffect(() => { if (activeChat) setShowSidebar(false); }, [activeChat]);

    useEffect(() => {
        // Body overflow is handled by overscroll-behavior in CSS
    }, [isJoined]);


    useEffect(() => {
        const bye = () => ws.current?.readyState === WebSocket.OPEN && ws.current.close();
        window.addEventListener('beforeunload', bye);
        return () => { window.removeEventListener('beforeunload', bye); bye(); };
    }, []);

    const handleJoin = (e, targetRoomId) => {
        if (e) e.preventDefault();
        if (!userName.trim()) return;
        const currentRoomId = targetRoomId || roomId || 'global';
        setRoomId(currentRoomId);

        // Intelligent Hybrid WS detection for Whisper Mode
        let wsApiBase;
        const isLocalIP = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(window.location.hostname);
        const isLocalhost = window.location.hostname === 'localhost';

        if (isLocalIP || isLocalhost) {
            // If we're on Vite dev port 5173, point to backend port 5000
            if (window.location.port === '5173') {
                wsApiBase = `${window.location.protocol}//${window.location.hostname}:5000`;
            } else {
                wsApiBase = `${window.location.protocol}//${window.location.host}`;
            }
        } else {
            // LIVE: Point directly to the Render backend domain for WebSockets
            // This is required because Netlify's domain (heksta.in) doesn't support WebSockets
            wsApiBase = 'https://heksta-backend-final.onrender.com';
        }

        const wsProtocol = wsApiBase.startsWith('https') ? 'wss:' : 'ws:';
        const wsUrl = `${wsProtocol}//${wsApiBase.replace(/^https?:\/\//, '').replace(/\/api$/, '')}/whisper`;
        ws.current = new WebSocket(wsUrl);

        ws.current.onopen = () => {
            ws.current.send(JSON.stringify({ type: 'join', name: userName, roomId: currentRoomId }));
            setIsJoined(true);
        };
        ws.current.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'connected') {
                    setMyClientId(data.clientId);
                } else if (data.type === 'user_list') {
                    setUserList(data.users);
                } else if (data.type === 'private_message') {
                    const sid = data.from;
                    setMessages(prev => ({ ...prev, [sid]: [...(prev[sid] || []), { kind: 'text', sender: 'them', senderName: data.fromName, content: data.content, timestamp: data.timestamp }] }));
                    if (activeChatRef.current?.id !== sid)
                        setUnreadCounts(prev => ({ ...prev, [sid]: (prev[sid] || 0) + 1 }));
                } else if (data.type === 'file_message') {
                    const sid = data.from;
                    setMessages(prev => ({ ...prev, [sid]: [...(prev[sid] || []), { kind: 'file', sender: 'them', senderName: data.fromName, fileId: data.fileId, fileName: data.fileName, fileSize: data.fileSize, mimeType: data.mimeType, timestamp: data.timestamp }] }));
                    if (activeChatRef.current?.id !== sid)
                        setUnreadCounts(prev => ({ ...prev, [sid]: (prev[sid] || 0) + 1 }));
                } else if (data.type === 'typing') {
                    const sid = data.from;
                    if (data.isTyping) {
                        setRemoteTypingMap(prev => ({ ...prev, [sid]: Date.now() }));
                        // Auto clear after 3 seconds if no more typing messages
                        if (typingTimeoutRef.current[sid]) clearTimeout(typingTimeoutRef.current[sid]);
                        typingTimeoutRef.current[sid] = setTimeout(() => {
                            setRemoteTypingMap(prev => {
                                const next = { ...prev };
                                delete next[sid];
                                return next;
                            });
                        }, 3000);
                    } else {
                        setRemoteTypingMap(prev => {
                            const next = { ...prev };
                            delete next[sid];
                            return next;
                        });
                    }
                } else if (data.type === 'private_file_chunk') {
                    const sid = data.from;
                    if (!incomingChunks.current[data.fileId]) {
                        incomingChunks.current[data.fileId] = [];
                    }
                    incomingChunks.current[data.fileId].push(data.chunk);

                    if (data.isLast) {
                        const chunks = incomingChunks.current[data.fileId];
                        // Convert base64 data URLs back to a single Blob
                        const byteArrays = chunks.map(chunk => {
                            const byteString = atob(chunk.split(',')[1]);
                            const byteNumbers = new Array(byteString.length);
                            for (let i = 0; i < byteString.length; i++) {
                                byteNumbers[i] = byteString.charCodeAt(i);
                            }
                            return new Uint8Array(byteNumbers);
                        });

                        const blob = new Blob(byteArrays, { type: data.fileType });
                        const fileUrl = URL.createObjectURL(blob);

                        setMessages(prev => ({
                            ...prev, [sid]: [...(prev[sid] || []), {
                                kind: 'file', sender: 'them', senderName: data.fromName,
                                fileId: data.fileId, fileName: data.fileName, fileSize: data.fileSize,
                                mimeType: data.fileType, fileUrl, isStreamed: true, timestamp: data.timestamp
                            }]
                        }));

                        delete incomingChunks.current[data.fileId];
                        if (activeChatRef.current?.id !== sid)
                            setUnreadCounts(prev => ({ ...prev, [sid]: (prev[sid] || 0) + 1 }));
                    }
                }
            } catch (_) { }
        };
        ws.current.onclose = () => { setIsJoined(false); setUserList([]); setActiveChat(null); setMessages({}); setUnreadCounts({}); setMyClientId(null); };
        ws.current.onerror = () => alert('⚠️ Connection failed. Is the backend running?');
    };

    const openChat = useCallback((user) => {
        setActiveChat(user);
        setShowSidebar(false);
        setUnreadCounts(prev => ({ ...prev, [user.id]: 0 }));
        setRemoteTypingMap(prev => {
            const next = { ...prev };
            delete next[user.id];
            return next;
        });
        setTimeout(() => inputRef.current?.focus(), 100);
    }, []);

    const lastTypingSentRef = useRef(0);
    const handleInputChange = (e) => {
        setInputText(e.target.value);
        if (!activeChat || ws.current?.readyState !== WebSocket.OPEN) return;

        const now = Date.now();
        if (now - lastTypingSentRef.current > 1500) {
            ws.current.send(JSON.stringify({ type: 'typing', to: activeChat.id, isTyping: true }));
            lastTypingSentRef.current = now;
        }
    };

    const sendMessage = (e) => {
        e.preventDefault();
        const text = inputText.trim();
        if (!text || !activeChat || ws.current?.readyState !== WebSocket.OPEN) return;

        // Stop typing indicator on send
        ws.current.send(JSON.stringify({ type: 'typing', to: activeChat.id, isTyping: false }));
        lastTypingSentRef.current = 0;

        ws.current.send(JSON.stringify({ type: 'private_message', to: activeChat.id, content: text }));
        setMessages(prev => ({ ...prev, [activeChat.id]: [...(prev[activeChat.id] || []), { kind: 'text', sender: 'me', content: text, timestamp: Date.now() }] }));
        setInputText('');
    };

    // File picking
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
        setPendingFile({ file, previewUrl });
        e.target.value = '';
    };

    const cancelFile = () => {
        if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
        setPendingFile(null);
    };

    const [transferProgress, setTransferProgress] = useState(0);

    const sendFile = async () => {
        if (!pendingFile || !activeChat || ws.current?.readyState !== WebSocket.OPEN) return;

        const file = pendingFile.file;
        const fileId = uuidv4();
        const chunkSize = 64 * 1024; // 64KB per chunk for optimal speed
        const totalChunks = Math.ceil(file.size / chunkSize);

        setUploading(true);
        setTransferProgress(0);

        // ── METHOD 1: High-Speed WebSocket Streaming for Large Files ──
        if (file.size > 2 * 1024 * 1024) { // Use WebSocket for files > 2MB
            const reader = new FileReader();
            let chunkIndex = 0;

            const readNextChunk = () => {
                const start = chunkIndex * chunkSize;
                const end = Math.min(start + chunkSize, file.size);
                const blob = file.slice(start, end);
                reader.readAsDataURL(blob);
            };

            reader.onload = (e) => {
                const base64Chunk = e.target.result;
                ws.current.send(JSON.stringify({
                    type: 'private_file_chunk',
                    to: activeChat.id,
                    chunk: base64Chunk,
                    fileId,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    isLast: chunkIndex === totalChunks - 1
                }));

                chunkIndex++;
                setTransferProgress(Math.round((chunkIndex / totalChunks) * 100));

                if (chunkIndex < totalChunks) {
                    // Small delay to prevent blocking the event loop
                    setTimeout(readNextChunk, 5);
                } else {
                    setUploading(false);
                    setTransferProgress(0);
                    setMessages(prev => ({
                        ...prev,
                        [activeChat.id]: [...(prev[activeChat.id] || []), {
                            kind: 'file', sender: 'me', fileId, fileName: file.name, fileSize: file.size, mimeType: file.type, isStreamed: true, timestamp: Date.now()
                        }]
                    }));
                    cancelFile();
                }
            };

            readNextChunk();
            return;
        }

        // ── METHOD 2: Classic Fetch for Small Files (Persistent) ──
        try {
            const formData = new FormData();
            formData.append('file', pendingFile.file);
            formData.append('senderId', myClientId || 'unknown');
            formData.append('roomId', roomId || 'global');

            const res = await fetch(`${API_BASE}/api/whisper/upload`, { method: 'POST', body: formData });

            const contentType = res.headers.get('content-type');
            if (!res.ok || !contentType || !contentType.includes('application/json')) {
                const text = await res.text();
                throw new Error(text.includes('<!DOCTYPE') ? `Server error (${res.status})` : (text || 'Upload failed'));
            }

            const { fileId, fileName, fileSize, mimeType } = await res.json();
            ws.current.send(JSON.stringify({ type: 'file_message', to: activeChat.id, fileId, fileName, fileSize, mimeType }));
            setMessages(prev => ({ ...prev, [activeChat.id]: [...(prev[activeChat.id] || []), { kind: 'file', sender: 'me', fileId, fileName, fileSize, mimeType, timestamp: Date.now() }] }));
            cancelFile();
        } catch (err) {
            alert('❌ File upload failed: ' + err.message);
        } finally {
            setUploading(false);
        }
    };

    const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);
    const filteredUsers = userList.filter(u => u.name.toLowerCase().includes(searchQuery.toLowerCase()));

    const placeholderStyle = `
        .whisper-input::placeholder{color:${T.placeholder}!important;opacity:1}
        .whisper-search::placeholder{color:${T.placeholder}!important;opacity:1}
        @keyframes typingPulse {
            0% { transform: scale(0.8); opacity: 0.4; }
            50% { transform: scale(1.2); opacity: 1; }
            100% { transform: scale(0.8); opacity: 0.4; }
        }
        .typing-indicator-premium {
            display: flex;
            align-items: center;
            gap: 3px;
            padding: 4px 10px;
            background: ${T.accentBg};
            border-radius: 12px;
            border: 1px solid ${T.accentBorder};
        }
        .typing-indicator-premium span {
            width: 5px;
            height: 5px;
            background-color: ${T.accent};
            border-radius: 50%;
            animation: typingPulse 1s infinite ease-in-out;
        }
        .typing-indicator-premium span:nth-child(2) { animation-delay: 0.2s; }
        .typing-indicator-premium span:nth-child(3) { animation-delay: 0.4s; }
    `;

    const TypingIndicator = () => (
        <div className="typing-indicator-premium animate-fade-in">
            <span></span><span></span><span></span>
        </div>
    );


    // ── FILE BUBBLE ──
    const FileBubble = ({ msg, isMe }) => {
        const isImage = msg.mimeType?.startsWith('image/');
        const fileUrl = `${API_BASE}/api/whisper/file/${msg.fileId}`;
        return (
            <div style={{
                background: isMe ? T.msgMe : T.fileBg,
                border: `1px solid ${isMe ? 'transparent' : T.accentBorder}`,
                color: isMe ? T.msgMeText : T.text,
            }} className={`rounded-2xl overflow-hidden shadow-md ${isMe ? 'rounded-br-none' : 'rounded-bl-none'}`}>
                {isImage ? (
                    <a href={fileUrl} target="_blank" rel="noreferrer">
                        <img src={fileUrl} alt={msg.fileName} className="max-w-[220px] max-h-48 object-cover block" />
                    </a>
                ) : null}
                <div className="px-3 py-2 flex items-center gap-2">
                    <span className="text-xl flex-shrink-0">{getFileIcon(msg.mimeType, msg.fileName)}</span>
                    <div className="overflow-hidden flex-1">
                        <p className="text-xs font-bold truncate" title={msg.fileName}>{msg.fileName}</p>
                        <p style={{ color: isMe ? 'rgba(255,255,255,0.6)' : T.muted }} className="text-[10px]">{formatBytes(msg.fileSize)}</p>
                    </div>
                    <a
                        href={fileUrl}
                        download={msg.fileName}
                        target="_blank"
                        rel="noreferrer"
                        style={{ background: isMe ? 'rgba(255,255,255,0.2)' : T.accentBg, color: isMe ? '#fff' : T.accent }}
                        className="text-xs font-bold px-2 py-1 rounded-lg flex-shrink-0 hover:opacity-80 transition-all no-underline"
                    >⬇</a>
                </div>
                <p style={{ color: isMe ? 'rgba(255,255,255,0.4)' : T.muted }} className="text-[9px] px-3 pb-1.5 text-right">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
            </div>
        );
    };

    // ── JOIN SCREEN ──
    if (!isJoined) {
        return (
            <div style={{ background: T.bg }} className="min-h-[80vh] flex items-center justify-center px-4 py-12">
                <style>{placeholderStyle}</style>
                <div style={{ background: T.sidebar, border: `1px solid ${T.border}` }} className="w-full max-w-sm rounded-3xl p-8 shadow-2xl text-center">
                    <div className="flex justify-end mb-4">
                        <button onClick={toggleTheme} style={{ background: T.card, border: `1px solid ${T.border}`, color: T.subtext }} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-80 transition-all">
                            {T.icon} {T.label}
                        </button>
                    </div>
                    <div className="w-20 h-20 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-4xl shadow-[0_0_30px_rgba(16,185,129,0.3)]">🤫</div>
                    <h1 style={{ color: T.text }} className="text-3xl font-black mb-2 italic tracking-tight">Whisper Mode</h1>
                    <p style={{ color: T.muted }} className="mb-8 text-sm font-medium uppercase tracking-widest">Enter the Shadows</p>

                    {joinStep === 'initial' && (
                        <div className="space-y-4">
                            <input type="text" value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="Your Ghost Name" className="whisper-input w-full rounded-2xl py-4 px-6 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-base" style={{ background: T.inputBg, border: `1px solid ${T.inputBorder}`, color: T.text, caretColor: T.accent }} required autoFocus maxLength={30} />

                            <button onClick={() => { if (!userName.trim()) return alert('Enter ghost name'); setRoomId('global'); handleJoin(null, 'global'); }} className="w-full bg-indigo-600 hover:bg-indigo-500 active:scale-95 font-bold py-3 rounded-xl shadow-lg shadow-indigo-500/20 transition-all text-sm" style={{ color: '#fff' }}>Enter Global Shadows</button>

                            <div className="flex gap-3 mt-4">
                                <button onClick={() => { if (!userName.trim()) return alert('Enter ghost name'); setJoinStep('create'); }} className="flex-1 bg-slate-800 hover:bg-slate-700 active:scale-95 font-bold py-3 rounded-xl transition-all border border-slate-700 text-sm" style={{ color: '#fff' }}>Create Room</button>
                                <button onClick={() => { if (!userName.trim()) return alert('Enter ghost name'); setJoinStep('joinCode'); }} className="flex-1 bg-slate-800 hover:bg-slate-700 active:scale-95 font-bold py-3 rounded-xl transition-all border border-slate-700 text-sm" style={{ color: '#fff' }}>Join Room</button>
                            </div>
                        </div>
                    )}

                    {joinStep === 'create' && (
                        <div className="space-y-4 animate-fade-in">
                            <p style={{ color: T.text }} className="text-sm font-bold">Generate a secure room code?</p>
                            <button onClick={() => { const code = Math.random().toString(36).substring(2, 8).toUpperCase(); setRoomId(code); handleJoin(null, code); }} className="w-full bg-indigo-600 hover:bg-indigo-500 active:scale-95 font-bold py-4 rounded-2xl shadow-lg transition-all" style={{ color: '#fff' }}>Generate & Enter</button>
                            <button onClick={() => setJoinStep('initial')} className="text-sm mt-2 hover:underline" style={{ color: T.muted }}>← Back</button>
                        </div>
                    )}

                    {joinStep === 'joinCode' && (
                        <form onSubmit={(e) => { e.preventDefault(); if (roomId.trim()) handleJoin(e, roomId.toUpperCase()); }} className="space-y-4 animate-fade-in">
                            <input type="text" value={roomId} onChange={(e) => setRoomId(e.target.value.toUpperCase())} placeholder="Room Code" className="whisper-input w-full rounded-2xl py-4 px-6 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-center transition-all text-base font-mono uppercase font-bold tracking-widest" style={{ background: T.inputBg, border: `1px solid ${T.inputBorder}`, color: T.text, caretColor: T.accent }} required autoFocus maxLength={10} />
                            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 active:scale-95 font-bold py-4 rounded-2xl shadow-lg transition-all" style={{ color: '#fff' }}>Join Dashboard</button>
                            <button type="button" onClick={() => setJoinStep('initial')} className="text-sm mt-2 hover:underline block w-full" style={{ color: T.muted }}>← Back</button>
                        </form>
                    )}

                    <p style={{ color: T.muted }} className="mt-6 text-xs">Files & messages deleted when you leave. No logs.</p>
                </div>
            </div>
        );
    }

    // ── DASHBOARD ──
    return (
        <div className="theme-green">
            <div style={{ background: T.bg, height: 'calc(100dvh - 70px)', display: 'flex', flexDirection: 'column' }} className="w-full px-0 sm:px-4 py-0 sm:py-4 overscroll-none overflow-hidden">
            <style>{placeholderStyle}</style>
            {/* Hidden file input */}
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange}
                accept="image/*,.pdf,.zip,.rar,.7z,.tar,.gz,.mp4,.mov,.mp3,.wav,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.js,.ts,.jsx,.tsx,.py,.json,.csv,.svg" />

            <div style={{ border: `1px solid ${T.border}`, height: '100%', minHeight: '500px' }} className="w-full max-w-[98%] xl:max-w-[1440px] mx-auto sm:rounded-3xl shadow-2xl overflow-hidden flex animate-fade-in relative flex-1">

                {/* ── SIDEBAR ── */}
                <div style={{ background: T.sidebar, borderRight: `1px solid ${T.border}` }} className={`flex-shrink-0 flex flex-col absolute inset-y-0 left-0 z-20 transition-transform duration-300 w-full sm:w-72 md:relative md:translate-x-0 md:w-72 md:z-auto ${showSidebar ? 'translate-x-0' : '-translate-x-full'}`}>
                    <div style={{ borderBottom: `1px solid ${T.border}` }} className="p-4 flex-shrink-0">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse"></span>
                                <h2 style={{ color: T.text }} className="font-bold tracking-tight text-sm">
                                    {(!roomId || roomId === 'global') ? 'Global Shadows' : `Room: ${roomId}`}
                                </h2>
                                {roomId && roomId !== 'global' && (
                                    <button onClick={() => { navigator.clipboard.writeText(roomId); alert('Copied!'); }} className="text-xs hover:opacity-70 transition-opacity" title="Copy Room Code">📋</button>
                                )}
                                <span style={{ background: T.accentBg, color: '#818cf8' }} className="text-xs px-2 py-0.5 rounded-full font-bold ml-1">{userList.length}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={toggleTheme} style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text }} className="text-base w-9 h-9 rounded-xl flex items-center justify-center hover:opacity-70 transition-all" title={T.label}>{T.icon}</button>
                                {activeChat && <button onClick={() => setShowSidebar(false)} style={{ color: T.subtext, background: T.card, border: `1px solid ${T.border}` }} className="md:hidden text-lg w-9 h-9 rounded-xl flex items-center justify-center hover:opacity-70">✕</button>}
                            </div>
                        </div>
                        <div className="relative">
                            <input type="text" placeholder="Search shadows..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="whisper-search w-full rounded-xl py-2 px-4 pl-8 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" style={{ background: T.inputBg, border: `1px solid ${T.inputBorder}`, color: T.text, caretColor: T.accent }} />
                            <span className="absolute left-2.5 top-2.5 text-xs" style={{ color: T.muted }}>🔍</span>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-1 custom-scrollbar">
                        {filteredUsers.length === 0 ? (
                            <div style={{ color: T.muted }} className="text-center py-12 italic text-sm">{userList.length === 0 ? 'Waiting for others...' : 'No results'}</div>
                        ) : filteredUsers.map(user => {
                            const count = unreadCounts[user.id] || 0;
                            const lastMsg = messages[user.id]?.at(-1);
                            const isActive = activeChat?.id === user.id;
                            return (
                                <button key={user.id} onClick={() => openChat(user)} style={{ background: isActive ? T.accentBg : 'transparent', border: `1px solid ${isActive ? T.accentBorder : 'transparent'}` }} className="w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left hover:opacity-80">
                                    <div style={{ background: isActive ? T.accent : T.card, border: `1px solid ${T.border}`, color: isActive ? '#fff' : T.subtext }} className="relative w-10 h-10 rounded-xl flex items-center justify-center font-bold text-base flex-shrink-0">
                                        {user.name.charAt(0).toUpperCase()}
                                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full" style={{ border: `2px solid ${T.sidebar}` }}></span>
                                    </div>
                                    <div className="overflow-hidden flex-1">
                                        <div className="flex justify-between items-center">
                                            <p style={{ color: count > 0 ? T.text : T.subtext }} className="text-sm font-bold truncate">{user.name}</p>
                                            {remoteTypingMap[user.id] && <TypingIndicator />}
                                        </div>
                                        <p style={{ color: T.muted }} className="text-[11px] truncate">
                                            {remoteTypingMap[user.id] ? 'whispering...' : (lastMsg ? (lastMsg.kind === 'file' ? `${lastMsg.sender === 'me' ? 'You: ' : ''}📎 ${lastMsg.fileName}` : (lastMsg.sender === 'me' ? `You: ${lastMsg.content}` : lastMsg.content)) : 'Tap to whisper')}
                                        </p>
                                    </div>
                                    {count > 0 && <span className="flex-shrink-0 min-w-[20px] h-5 bg-indigo-500 text-[10px] font-black rounded-full flex items-center justify-center px-1.5" style={{ color: '#fff' }}>{count > 99 ? '99+' : count}</span>}
                                </button>
                            );
                        })}
                    </div>

                    <div style={{ borderTop: `1px solid ${T.border}`, background: T.sidebar }} className="p-3 flex items-center gap-3 flex-shrink-0">
                        <div className="w-9 h-9 bg-indigo-700 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0" style={{ color: '#fff' }}>{userName.charAt(0).toUpperCase()}</div>
                        <div className="overflow-hidden flex-1">
                            <p style={{ color: T.text }} className="text-sm font-bold truncate">{userName}</p>
                            <p className="text-[10px] font-medium tracking-wider" style={{ color: '#4ade80' }}>● YOU · ONLINE</p>
                        </div>
                        <button onClick={() => ws.current?.close()} title="Leave" style={{ color: T.muted }} className="hover:text-red-400 transition-colors text-xl leading-none">⏻</button>
                    </div>
                </div>

                {/* ── CHAT AREA ── */}
                <div style={{ background: T.chat }} className="flex-1 flex flex-col min-w-0">
                    {activeChat ? (
                        <>
                            {/* Header */}
                            <div style={{ background: T.header, borderBottom: `1px solid ${T.border}` }} className="px-4 py-3 backdrop-blur-md flex items-center gap-3 flex-shrink-0">
                                <button onClick={() => setShowSidebar(true)} style={{ color: T.subtext }} className="md:hidden text-lg mr-0.5 flex-shrink-0 hover:opacity-70">←</button>
                                <div className="relative w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ color: '#fff' }}>
                                    {activeChat.name.charAt(0).toUpperCase()}
                                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full" style={{ border: `2px solid ${T.chat}` }}></span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 style={{ color: T.text }} className="font-bold truncate text-sm">{activeChat.name}</h3>
                                        {remoteTypingMap[activeChat.id] && <TypingIndicator />}
                                    </div>
                                    <p className="text-[10px] font-medium tracking-widest" style={{ color: remoteTypingMap[activeChat.id] ? T.accent : '#818cf8' }}>
                                        {remoteTypingMap[activeChat.id] ? 'TYPING...' : 'PRIVATE · EPHEMERAL'}
                                    </p>
                                </div>
                                {(messages[activeChat.id]?.length > 0) && (
                                    <span style={{ color: T.muted, background: T.card, border: `1px solid ${T.border}` }} className="text-[10px] px-2 py-1 rounded-full flex-shrink-0">{messages[activeChat.id].length} msg{messages[activeChat.id].length !== 1 ? 's' : ''}</span>
                                )}
                                <button onClick={() => { setActiveChat(null); setShowSidebar(true); }} style={{ color: T.muted }} className="p-1.5 rounded-lg hover:opacity-70 flex-shrink-0">✕</button>
                            </div>

                            {/* Messages */}
                            <div
                                ref={messagesContainerRef}
                                className="flex-1 overflow-y-auto p-3 sm:p-5 space-y-4 custom-scrollbar"
                                style={{ overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch' }}
                            >
                                {!messages[activeChat.id]?.length ? (
                                    <div className="h-full flex flex-col items-center justify-center opacity-25">
                                        <span className="text-5xl mb-3">🤐</span>
                                        <p style={{ color: T.subtext }} className="font-bold tracking-widest uppercase text-xs text-center">Start whispering...</p>
                                    </div>
                                ) : messages[activeChat.id].map((msg, idx) => {
                                    const isMe = msg.sender === 'me';
                                    return (
                                        <div key={idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'} animate-scale-in items-end gap-1.5`}>
                                            {!isMe && <div style={{ background: T.card, border: `1px solid ${T.border}`, color: T.subtext }} className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 mb-0.5">{activeChat.name.charAt(0).toUpperCase()}</div>}
                                            <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} max-w-[78%] sm:max-w-[68%]`}>
                                                <span className="text-[9px] mb-0.5 font-medium mx-1" style={{ color: isMe ? '#818cf8' : T.muted }}>#{idx + 1}</span>
                                                {msg.kind === 'file' ? (
                                                    <FileBubble msg={msg} isMe={isMe} />
                                                ) : (
                                                    <div style={{ background: isMe ? T.msgMe : T.msgThem, color: isMe ? T.msgMeText : T.msgThemText, border: isMe ? 'none' : `1px solid ${T.border}` }} className={`rounded-2xl px-4 py-2.5 shadow-md ${isMe ? 'rounded-br-none' : 'rounded-bl-none'}`}>
                                                        <p className="text-sm leading-relaxed break-words">{msg.content}</p>
                                                        <p className={`text-[9px] mt-1 ${isMe ? 'text-right' : 'text-left'}`} style={{ color: isMe ? 'rgba(255,255,255,0.55)' : T.muted }}>
                                                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                            {isMe && <div style={{ background: T.accentBg, border: `1px solid ${T.accentBorder}`, color: '#818cf8' }} className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 mb-0.5">{userName.charAt(0).toUpperCase()}</div>}
                                        </div>
                                    );
                                })}
                                <div className="h-0.5 w-full shrink-0" />
                            </div>

                            {/* Pending file preview */}
                            {pendingFile && (
                                <div style={{ background: T.accentBg, borderTop: `1px solid ${T.accentBorder}` }} className="px-4 py-3 flex items-center gap-3 flex-shrink-0">
                                    {pendingFile.previewUrl ? (
                                        <img src={pendingFile.previewUrl} alt="preview" className="w-12 h-12 object-cover rounded-xl flex-shrink-0" />
                                    ) : (
                                        <div style={{ background: T.card, border: `1px solid ${T.border}` }} className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                                            {getFileIcon(pendingFile.file.type, pendingFile.file.name)}
                                        </div>
                                    )}
                                    <div className="flex-1 overflow-hidden">
                                        <p style={{ color: T.text }} className="text-xs font-bold truncate">{pendingFile.file.name}</p>
                                        <p style={{ color: T.muted }} className="text-[10px]">{formatBytes(pendingFile.file.size)}</p>
                                    </div>
                                    <button onClick={cancelFile} style={{ color: T.muted }} className="hover:text-red-400 transition-colors text-lg flex-shrink-0">✕</button>
                                    <button onClick={sendFile} disabled={uploading} className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 font-bold text-xs px-4 py-2 rounded-xl transition-all flex-shrink-0" style={{ color: '#fff' }}>
                                        {uploading ? 'Sending...' : 'Send 📤'}
                                    </button>
                                </div>
                            )}

                            {/* Input bar - Static at bottom */}
                            <div style={{ background: T.inputBar, borderTop: `1px solid ${T.border}` }} className="p-3 sm:p-4 mt-auto flex-shrink-0 z-10 w-full">
                                <form onSubmit={sendMessage} className="flex gap-2 items-center">
                                    {/* Attach button */}
                                    <button type="button" onClick={() => fileInputRef.current?.click()} style={{ background: T.card, border: `1px solid ${T.border}`, color: T.subtext }} className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 hover:opacity-70 transition-all text-lg" title="Attach file">📎</button>
                                    <input ref={inputRef} type="text" value={inputText} onChange={handleInputChange} placeholder={`Whisper to ${activeChat.name}...`} className="whisper-input flex-1 rounded-xl py-3 px-4 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-sm min-w-0" style={{ background: T.inputBg, border: `1px solid ${T.inputBorder}`, color: T.text, caretColor: T.accent }} autoComplete="off" />
                                    <button type="submit" disabled={!inputText.trim()} className="w-10 h-10 sm:w-auto sm:px-5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed font-bold rounded-xl transition-all active:scale-95 flex-shrink-0 flex items-center justify-center" style={{ color: '#fff' }}>
                                        <span className="hidden sm:inline text-sm">SEND</span>
                                        <span className="sm:hidden text-base">↑</span>
                                    </button>
                                </form>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                            <div style={{ background: T.card, border: `1px solid ${T.border}` }} className="w-20 h-20 rounded-3xl flex items-center justify-center text-4xl mb-5 animate-pulse">🤫</div>
                            <h3 style={{ color: T.text }} className="text-lg font-bold mb-2">Select a Shadow</h3>
                            <p style={{ color: T.subtext }} className="max-w-xs text-sm leading-relaxed">Choose someone for a private, encrypted, ephemeral conversation with text & file sharing.</p>
                            {totalUnread > 0 && (
                                <div style={{ background: T.accentBg, border: `1px solid ${T.accentBorder}` }} className="mt-4 rounded-xl px-4 py-3">
                                    <p className="font-bold text-sm" style={{ color: '#818cf8' }}>📬 {totalUnread} unread message{totalUnread !== 1 ? 's' : ''}</p>
                                </div>
                            )}
                            <button onClick={() => setShowSidebar(true)} className="md:hidden mt-5 bg-indigo-600 hover:bg-indigo-500 font-bold py-3 px-6 rounded-xl transition-all active:scale-95 text-sm" style={{ color: '#fff' }}>
                                View Live Users ({userList.length})
                            </button>
                        </div>
                    )}
                </div>
            </div>
            </div>
        </div>
    );
};
